import type { Group } from './types';

// Geotab's short-form legacy built-in group IDs — kept as an explicit list since they don't
// follow the `Group<Name>Id` naming pattern below.
export const SYSTEM_GROUP_IDS = new Set([
  'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7',
]);

// All of Geotab's built-in/system groups (Company, Vehicles, Drivers, Asset Information,
// the EV powertrain auto-classification groups, etc.) use a reserved `Group<Name>Id` naming
// convention, e.g. `GroupCompanyId`, `GroupAssetInformationId`, `GroupElectricHybridPlugInId`.
// Real customer-created groups get opaque auto-generated IDs (e.g. `b1A2B3C4`) that never
// match this shape, so the pattern reliably separates "built-in" from "your org's own groups"
// without needing to hardcode every built-in ID Geotab ships (new ones keep appearing, e.g.
// per-tenant EV classification groups).
const BUILT_IN_GROUP_ID_PATTERN = /^Group[A-Z]\w*Id$/;

export function isSystemGroupId(id: string): boolean {
  return SYSTEM_GROUP_IDS.has(id) || BUILT_IN_GROUP_ID_PATTERN.test(id);
}

export interface GroupNode {
  id:    string;
  name:  string;
  depth: number;   // 0 = top-level (under a system root); used for indenting a dropdown
}

export interface GroupHierarchy {
  groupMap:      Map<string, string>;    // groupId → group name
  groups:        GroupNode[];            // every non-system group, in tree order
  groupChildren: Map<string, string[]>;  // groupId → direct child ids (for descendant expansion)
  parentOf:      Map<string, string>;    // groupId → immediate parent id (for rolling a leaf up to an ancestor)
}

// Build the full group tree from each group's `children` array so parent groups (vehicles
// usually sit only in the leaf groups) and empty groups are listed too, plus a groupChildren
// map so selecting a parent can expand to all its descendant groups when filtering.
export function buildGroupHierarchy(rawGroups: Group[]): GroupHierarchy {
  const groupMap = new Map<string, string>();
  rawGroups.forEach(g => { if (g.id) groupMap.set(g.id, g.name || g.id); });

  const known = new Set(rawGroups.map(g => g.id).filter(Boolean) as string[]);
  const groupChildren = new Map<string, string[]>();
  const parentOf      = new Map<string, string>();
  rawGroups.forEach(g => {
    if (!g.id) return;
    const kids = (g.children ?? [])
      .map(c => c.id)
      .filter((id): id is string => !!id && known.has(id));
    if (kids.length) groupChildren.set(g.id, kids);
    kids.forEach(cid => parentOf.set(cid, g.id!));
  });

  const isSystem = isSystemGroupId;
  const byName   = (a: string, b: string) => (groupMap.get(a) ?? a).localeCompare(groupMap.get(b) ?? b);

  // Display roots = non-system groups whose parent is missing or a system group (e.g. Company).
  const roots = rawGroups
    .map(g => g.id)
    .filter((id): id is string => !!id && !isSystem(id))
    .filter(id => { const p = parentOf.get(id); return !p || isSystem(p); })
    .sort(byName);

  const groups: GroupNode[] = [];
  const visited = new Set<string>();
  const walk = (id: string, depth: number): void => {
    if (isSystem(id) || visited.has(id)) return;
    visited.add(id);
    groups.push({ id, name: groupMap.get(id) ?? id, depth });
    (groupChildren.get(id) ?? [])
      .filter(cid => !isSystem(cid))
      .sort(byName)
      .forEach(cid => walk(cid, depth + 1));
  };
  roots.forEach(r => walk(r, 0));

  // Safety net: list any non-system group not reached through the tree (orphaned child refs,
  // or data where `children` isn't populated) so nothing is ever silently dropped.
  rawGroups
    .map(g => g.id)
    .filter((id): id is string => !!id && !isSystem(id) && !visited.has(id))
    .sort(byName)
    .forEach(id => { visited.add(id); groups.push({ id, name: groupMap.get(id) ?? id, depth: 0 }); });

  return { groupMap, groups, groupChildren, parentOf };
}

// Expand a set of selected groups to include all of their descendant groups, so selecting a
// parent group matches vehicles that sit in its child groups.
export function expandGroups(selected: Set<string>, children: Map<string, string[]>): Set<string> {
  const out = new Set<string>();
  const stack = [...selected];
  while (stack.length) {
    const id = stack.pop()!;
    if (out.has(id)) continue;
    out.add(id);
    const kids = children.get(id);
    if (kids) for (const c of kids) if (!out.has(c)) stack.push(c);
  }
  return out;
}

// Walk from a group up to the root, returning the *shallowest* ancestor (closest to the root)
// that is itself in `selected`. Used to roll a vehicle's specific leaf group up to whatever
// level the user actually checked in a group filter — checking only a top-level parent rolls
// every descendant vehicle up to that one bucket, while checking a narrower sub-group keeps
// buckets at that finer level instead. Falls back to `id` itself if nothing in the chain (up to
// and including `id`) is selected.
export function shallowestSelectedAncestor(
  id: string,
  selected: Set<string>,
  parentOf: Map<string, string>
): string {
  let result = id;
  let current = id;
  while (true) {
    if (selected.has(current)) result = current;
    const parent = parentOf.get(current);
    if (!parent) break;
    current = parent;
  }
  return result;
}
